import { Box, Checkbox, FormControlLabel } from "@mui/material";
import React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import MeasureResultField from "./MeasureResultField";

interface OrthogonalDistance {
  id: string;
  label: string;
  value: string;
}

export default function OrthogonalDistanceContent() {
  const { t } = useTranslation();

  const darkTheme = defaultStore((state) => state.darkTheme);

  const showSmallToolContent = sidebarStore(
    (state) => state.showSmallToolContent
  );
  const orthogonalDistanceTool = sidebarStore(
    (state) => state.orthogonalDistanceTool
  );
  const orthogonalDistanceToolValues = sidebarStore(
    (state) => state.orthogonalDistanceToolValues
  );
  const orthogonalDistanceActiveAttributes = sidebarStore(
    (state) => state.orthogonalDistanceActiveAttributes
  );
  const changeOrthogonalDistanceActiveAttributes = sidebarStore(
    (state) => state.changeOrthogonalDistanceActiveAttributes
  );

  const defaultValue = "0.00";
  const [distance, setDistance] = useState<string>(defaultValue);

  const [referenceLineSet, setReferenceLineSet] = useState<boolean>(false);

  //update states for height
  useEffect(() => {
    if (orthogonalDistanceToolValues !== undefined) {
      setDistance(orthogonalDistanceToolValues.distance.toFixed(2));
    } else {
      setDistance(defaultValue);
    }
  }, [orthogonalDistanceToolValues]);

  useEffect(() => {
    const onReferenceLineSet = () => {
      setReferenceLineSet(true);
    };
    orthogonalDistanceTool?.on("referencelineset", onReferenceLineSet);

    const onReset = () => {
      setReferenceLineSet(false);
    };
    orthogonalDistanceTool?.on("reset", onReset);

    // Cleanup
    return () => {
      orthogonalDistanceTool?.off("referencelineset", onReferenceLineSet);
      orthogonalDistanceTool?.off("reset", onReset);
      setReferenceLineSet(false);
    };
  }, [orthogonalDistanceTool]);

  //attributes list
  const attributes: OrthogonalDistance[] = [
    {
      id: "distance",
      label: t("orthogonal.distance"),
      value: distance,
    },
  ];

  return (
    <Box
      width="100%"
      display={"flex"}
      alignItems={"start"}
      flexDirection={"column"}
      gap={0.5}
    >
      {!showSmallToolContent && (
        <FormControlLabel
          label={t("orthogonal.activate_auto_edge_detection")}
          sx={{
            "& .MuiFormControlLabel-label": {
              fontSize: "16px",
              color: darkTheme ? "white" : "darkblue.main",
            },
          }}
          control={
            <Checkbox
              size="small"
              classes={{
                root: darkTheme
                  ? "editFeatureRadioButtonAccentWhite"
                  : "editFeatureRadioButtonAccent",
                checked: darkTheme
                  ? "editFeatureRadioButtonAccentWhite"
                  : "editFeatureRadioButtonAccent",
              }}
              disabled={referenceLineSet}
              onChange={(evt) => {
                orthogonalDistanceTool?.setAutoEdgeDetection(
                  evt.target.checked
                );
              }}
              defaultChecked={orthogonalDistanceTool?.options.autoEdgeDetection}
            />
          }
        />
      )}
      {attributes.map((item: OrthogonalDistance) => (
        <MeasureResultField
          key={item.id}
          id={item.id}
          title={item.label}
          value={item.value + " m"}
          loggingString="OrthoDistance"
          activeAttributes={orthogonalDistanceActiveAttributes}
          changeActiveAttributes={changeOrthogonalDistanceActiveAttributes}
          showPin={attributes.length > 1}
        />
      ))}
    </Box>
  );
}
